import type {
  ArtifactRef,
  RunAttempt,
  RunEvent,
  RunStepResult,
  WebTestRun
} from '@blume/web-test-model'
import {
  Accessibility,
  Archive,
  CircleCheck,
  CircleX,
  Download,
  Eye,
  FileCode2,
  FileDiff,
  FileText,
  Film,
  LoaderCircle,
  Network,
  ScrollText,
  type LucideIcon
} from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { deriveLiveAttempts, derivePhaseSpine } from '@/live/live-model'
import { verifierBaseUrl, type RunFeed } from '@/live/use-run-feed'

import {
  CancellingNotice,
  LiveAnnouncement,
  LiveStatusControls,
  PhaseSpine,
  runOriginSentence
} from './live-run'

import {
  formatBytes,
  formatTime,
  originLabel,
  relativeTime,
  shortSha,
  targetLabel
} from '@/design/format'
import {
  fixtureArtifactImages,
  fixtureArtifactVideos,
  screenshotKindShortLabel
} from '@/design/fixture-artifacts'
import {
  classificationMeta,
  runStatusMeta,
  runVerdict,
  StatusInline,
  stepStatusMeta,
  toneText
} from '@/design/status'
import { cn } from '@/lib/utils'
import { RunAvatar, scenarioEmoji } from '@/ui/atoms/avatar-tile'
import { artifactRetentionLabel, formatDuration, stepPhaseLabel, titleCase } from '@/logic'

import {
  BackLink,
  CodeBlock,
  EmptyState,
  KeyValueList,
  MetaLine,
  SectionLabel,
  sourceLabel
} from './parts'

/* ------------------------------------------------------------------ */
/* Artifact presentation                                               */
/* ------------------------------------------------------------------ */

function artifactIcon(kind: ArtifactRef['kind']): LucideIcon {
  if (kind === 'recording') return Film
  if (kind === 'trace' || kind === 'junit' || kind === 'html-report') return Archive
  if (kind === 'accessibility-snapshot') return Accessibility
  if (kind === 'dom-excerpt') return FileCode2
  if (kind === 'network-log') return Network
  if (kind === 'console-log') return ScrollText
  return FileText
}

/** Plain-language labels for artifact kinds — no jargon at the top level. */
const artifactKindLabel: Record<string, string> = {
  trace: 'Playwright trace',
  recording: 'Video recording',
  'accessibility-snapshot': 'Accessibility snapshot',
  'dom-excerpt': 'DOM excerpt',
  'console-log': 'Console log',
  'network-log': 'Network log',
  'scenario-snapshot': 'Scenario snapshot',
  'plan-snapshot': 'Plan snapshot',
  'candidate-plan': 'Candidate plan',
  'plan-diff': 'Plan diff',
  'raw-events': 'Raw events',
  'result-json': 'Result JSON',
  junit: 'JUnit report',
  'html-report': 'HTML report'
}

function artifactDownloadUrl(runId: string, artifact: ArtifactRef): string {
  const path = `/v1/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifact.id)}`
  const verifier = verifierBaseUrl()
  // Artifact bytes are always requested from the verifier with the browser's
  // Better Auth cookie. Do not surface signed/token URLs in the dashboard.
  if (verifier) return `${verifier.replace(/\/+$/, '')}${path}`
  return artifact.downloadUrl ?? path
}

/* ------------------------------------------------------------------ */
/* Chronological stream model                                          */
/*                                                                     */
/* The run detail tells the run as ONE story: step results, run        */
/* events, screenshots, and video interleaved on a shared time axis.   */
/* Nothing lives in a hidden tab — evidence appears at the moment it   */
/* was captured, and raw event JSON is a per-event disclosure.         */
/* ------------------------------------------------------------------ */

interface StepEntry {
  kind: 'step'
  at: number
  step: RunStepResult
  artifacts: ArtifactRef[]
  events: RunEvent[]
}

interface EventEntry {
  kind: 'event'
  at: number
  event: RunEvent
}

interface ArtifactEntry {
  kind: 'artifact'
  at: number
  artifact: ArtifactRef
}

type AttemptItem = StepEntry | EventEntry | ArtifactEntry

interface QuietStepsGroup {
  kind: 'quiet-steps'
  at: number
  steps: StepEntry[]
}

interface AttemptSection {
  kind: 'attempt'
  at: number
  attempt: RunAttempt
  index: number
  total: number
  startSummary?: string
  items: Array<AttemptItem | QuietStepsGroup>
}

interface RunStream {
  start: number
  blocks: AttemptSection[]
  /** Technical run events that could not truthfully be assigned to a step. */
  unscopedEvents: EventEntry[]
  /** Artifacts referenced by no attempt or step — listed at the end. */
  unplacedArtifacts: ArtifactRef[]
}

function toTime(iso: string | undefined): number | undefined {
  if (!iso) return undefined
  const value = new Date(iso).getTime()
  return Number.isFinite(value) ? value : undefined
}

/** Collapse threshold: this many consecutive quiet passed steps fold up. */
const QUIET_GROUP_MIN = 4

function buildRunStream(run: WebTestRun, events: RunEvent[]): RunStream {
  const runEvents = [...events]
    .filter((event) => event.runId === run.id)
    .sort((left, right) => left.sequence - right.sequence)
  const start =
    toTime(run.startedAt) ?? toTime(run.createdAt) ?? toTime(runEvents[0]?.occurredAt) ?? 0
  const artifactById = new Map(run.artifacts.map((artifact) => [artifact.id, artifact]))
  const placedArtifactIds = new Set<string>()
  const consumedEventIds = new Set<string>()

  const attemptSections: AttemptSection[] = run.attempts.map((attempt, index) => {
    const attemptStart = toTime(attempt.startedAt) ?? start
    const startedEvent = runEvents.find(
      (event) => event.type === 'attempt-started' && event.attemptId === attempt.id
    )
    if (startedEvent) consumedEventIds.add(startedEvent.id)

    // Steps in execution order; times come from the step when reported and
    // are otherwise reconstructed from the attempt start plus durations.
    let cursor = attemptStart
    const stepEntries: StepEntry[] = attempt.steps.map((step) => {
      const at = toTime(step.startedAt) ?? cursor
      cursor = at + (step.durationMs ?? 0)
      const stepEvents = runEvents.filter(
        (event) => event.stepId === step.id && event.attemptId === attempt.id
      )
      for (const event of stepEvents) consumedEventIds.add(event.id)
      const artifacts = step.artifactIds.flatMap((id) => {
        const artifact = artifactById.get(id)
        if (!artifact) return []
        placedArtifactIds.add(id)
        return [artifact]
      })
      return { kind: 'step', at, step, artifacts, events: stepEvents }
    })

    // Events scoped to this attempt but not to a step (excluding the
    // attempt-started marker, which becomes the section header).
    const attemptEvents: EventEntry[] = []
    for (const event of runEvents) {
      if (
        event.attemptId === attempt.id &&
        event.stepId === undefined &&
        event.type !== 'attempt-started' &&
        !consumedEventIds.has(event.id)
      ) {
        consumedEventIds.add(event.id)
        attemptEvents.push({ kind: 'event', at: toTime(event.occurredAt) ?? attemptStart, event })
      }
    }

    // Attempt-level artifacts no step claimed (e.g. the full-attempt video
    // recording) — placed at their capture time.
    const attemptArtifacts: ArtifactEntry[] = attempt.artifactIds.flatMap((id) => {
      if (placedArtifactIds.has(id)) return []
      const artifact = artifactById.get(id)
      if (!artifact) return []
      placedArtifactIds.add(id)
      return [
        {
          kind: 'artifact' as const,
          at: toTime(artifact.createdAt) ?? attemptStart,
          artifact
        }
      ]
    })

    const items = [...stepEntries, ...attemptEvents, ...attemptArtifacts].sort(
      (left, right) => left.at - right.at
    )

    return {
      kind: 'attempt',
      at: attemptStart,
      attempt,
      index: index + 1,
      total: run.attempts.length,
      ...(startedEvent ? { startSummary: startedEvent.summary } : {}),
      items: collapseQuietSteps(items)
    }
  })

  const globalEvents: EventEntry[] = []
  for (const event of runEvents) {
    if (!consumedEventIds.has(event.id)) {
      globalEvents.push({ kind: 'event', at: toTime(event.occurredAt) ?? start, event })
    }
  }

  return {
    start,
    blocks: attemptSections,
    unscopedEvents: globalEvents,
    unplacedArtifacts: run.artifacts.filter((artifact) => !placedArtifactIds.has(artifact.id))
  }
}

/**
 * Long green runs must stay scannable: consecutive passed steps that carry
 * no error, no evidence, and no events fold into one visibly-labelled
 * disclosure. Failures, running steps, and anything with evidence never
 * collapse.
 */
function collapseQuietSteps(items: AttemptItem[]): Array<AttemptItem | QuietStepsGroup> {
  const isQuiet = (item: AttemptItem): item is StepEntry =>
    item.kind === 'step' &&
    (item.step.status === 'passed' || item.step.status === 'skipped') &&
    item.step.error === undefined &&
    item.artifacts.length === 0 &&
    item.events.length === 0

  const result: Array<AttemptItem | QuietStepsGroup> = []
  let quietRun: StepEntry[] = []
  const flush = () => {
    if (quietRun.length >= QUIET_GROUP_MIN) {
      result.push({ kind: 'quiet-steps', at: quietRun[0]!.at, steps: quietRun })
    } else {
      result.push(...quietRun)
    }
    quietRun = []
  }
  for (const item of items) {
    if (isQuiet(item)) quietRun.push(item)
    else {
      flush()
      result.push(item)
    }
  }
  flush()
  return result
}

/** Offset from run start rendered as m:ss — the stream's time axis. */
function formatOffset(at: number, start: number): string {
  const seconds = Math.max(0, Math.round((at - start) / 1_000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/* ------------------------------------------------------------------ */
/* Scan aids                                                           */
/* ------------------------------------------------------------------ */

interface JumpTarget {
  domId: string
  label: string
  fail?: boolean
}

/**
 * The stable outline of a long chronology: one entry per attempt boundary,
 * plus the first failed step. Attempts only ever append, so the list never
 * reorders under a live reader.
 */
function deriveJumpTargets(blocks: AttemptSection[]): JumpTarget[] {
  const targets: JumpTarget[] = []
  let firstFailure: JumpTarget | undefined
  for (const block of blocks) {
    if (block.kind !== 'attempt') continue
    targets.push({ domId: `attempt-${block.attempt.id}`, label: `Attempt ${block.index}` })
    if (!firstFailure) {
      for (const item of block.items) {
        if (item.kind === 'step' && item.step.status === 'failed') {
          firstFailure = { domId: `step-${item.step.id}`, label: 'First failure', fail: true }
          break
        }
      }
    }
  }
  return firstFailure ? [...targets, firstFailure] : targets
}

/**
 * Reader-initiated movement only: the stream itself never scrolls, but a
 * jump button is the reader choosing to move, so it may. Focus follows so
 * keyboard and screen-reader users land where they aimed.
 */
function jumpToStreamMoment(domId: string): void {
  const target = document.getElementById(domId)
  if (!target) return
  if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'start' })
  target.focus({ preventScroll: true })
}

/**
 * The scan aid itself: one quiet line of text buttons under the stream
 * intro. It only appears when the chronology has more than one landmark, and
 * it deliberately stays a single line of prose-weight links — an index into
 * the one stream, not a second navigation system.
 */
function JumpLine({ targets }: { targets: JumpTarget[] }) {
  if (targets.length < 2) return null
  return (
    <nav
      aria-label="Jump to a moment in this run"
      className="-mt-3 mb-6 flex flex-wrap items-center gap-x-4 gap-y-1"
    >
      <span className="text-[13px] text-muted-foreground">Jump to</span>
      {targets.map((target) => (
        <button
          key={target.domId}
          type="button"
          onClick={() => jumpToStreamMoment(target.domId)}
          className={cn(
            'inline-flex min-h-9 items-center gap-1.5 text-[13px] font-medium underline decoration-border underline-offset-4 transition-colors hover:decoration-current focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
            target.fail ? toneText.fail : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {target.fail ? <CircleX aria-hidden className="size-3.5 shrink-0" /> : null}
          {target.label}
        </button>
      ))}
    </nav>
  )
}

/**
 * A passed run whose runner reported only the verdict must still explain
 * itself: a compact two-line summary of what was verified, built entirely
 * from the run's own fields, in place of an empty-looking chronology. It is
 * honest about what was not kept.
 */
function PassedRunSummary({ run }: { run: WebTestRun }) {
  const target = targetLabel[run.target] ?? titleCase(run.target)
  return (
    <div className="max-w-prose">
      <p className="flex items-baseline gap-2.5 text-sm leading-6">
        <CircleCheck aria-hidden className="size-4 shrink-0 self-center text-muted-foreground" />
        <span>
          <span className="font-medium">{run.currentStageSummary ?? 'Every assertion held'}</span>
          {' — '}
          <span className="font-mono text-[13px]">{run.scenarioId}</span> verified against {target}
          {run.durationMs === undefined ? '' : ` in ${formatDuration(run.durationMs)}`}.
        </span>
      </p>
      <p className="mt-2 text-[13px] leading-6 text-muted-foreground">
        {run.planHash ? `Deterministic replay of committed plan ${shortSha(run.planHash)}. ` : ''}
        The runner reported only the verdict for this pass — no step-by-step narrative
        {run.artifacts.length === 0 ? ' and no evidence files were kept' : ''}.
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function RunDetailPage({
  run,
  events,
  now,
  feed
}: {
  run: WebTestRun | undefined
  events: RunEvent[]
  now: number
  feed?: RunFeed
}) {
  if (!run) {
    if (feed?.liveness === 'loading') {
      return (
        <>
          <BackLink href="#/runs" label="Runs" />
          <div aria-label="Loading run" className="max-w-3xl space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-5 w-3/5" />
            <p className="text-[13px] text-muted-foreground">Loading this run from the verifier…</p>
          </div>
        </>
      )
    }
    if (feed?.liveness === 'unauthorized') {
      return (
        <>
          <BackLink href="#/runs" label="Runs" />
          <EmptyState>
            You don’t have permission to see this run
            {feed.errorMessage ? ` — ${feed.errorMessage}` : ''}. Sign in to the verifier and
            reload.
          </EmptyState>
        </>
      )
    }
    return (
      <>
        <BackLink href="#/runs" label="Runs" />
        <EmptyState>This run does not exist or has been retained out.</EmptyState>
      </>
    )
  }
  return <RunDetail run={run} events={events} now={now} {...(feed ? { feed } : {})} />
}

function RunDetail({
  run,
  events,
  now,
  feed
}: {
  run: WebTestRun
  events: RunEvent[]
  now: number
  feed?: RunFeed
}) {
  const verdict = runVerdict(run)
  const classification = run.classification ? classificationMeta[run.classification] : undefined
  const headline = classification ?? verdict
  const HeadlineIcon = headline.icon
  const quietHeadline = headline.tone === 'pass' || headline.tone === 'neutral'
  // Live derivation: synthesize attempts/steps from the event stream when the
  // run's durable execution is absent or behind, so an in-flight run always
  // has a narrative. Terminal runs with full execution pass through unchanged.
  const derived = useMemo(() => deriveLiveAttempts(run, events), [run, events])
  const streamRun = useMemo(() => ({ ...run, attempts: derived.attempts }), [run, derived])
  const streamEvents = useMemo(
    () => events.filter((event) => !derived.consumedEventIds.has(event.id)),
    [events, derived]
  )
  const stream = useMemo(() => buildRunStream(streamRun, streamEvents), [streamRun, streamEvents])
  const hasNarrative = stream.blocks.length > 0 || stream.unscopedEvents.length > 0
  const jumpTargets = useMemo(() => deriveJumpTargets(stream.blocks), [stream])
  const spine = useMemo(() => derivePhaseSpine(run, events), [run, events])
  const active = run.state !== 'terminal'
  const showLivePanel = active || (feed?.watchedLive ?? false)
  const showJourney = !showLivePanel && spine.kind === 'authoring'
  // Candidate-plan and plan-diff artifacts render as inline review cards at
  // their moment in the stream; keep them out of the trailing artifact list.
  const inlinePlanArtifactIds = useMemo(() => {
    const ids = new Set<string>()
    const artifactsById = new Map(run.artifacts.map((artifact) => [artifact.id, artifact]))
    for (const event of streamEvents) {
      if (event.type !== 'artifact-created' || event.artifactId === undefined) continue
      const artifact = artifactsById.get(event.artifactId)
      if (artifact && (artifact.kind === 'candidate-plan' || artifact.kind === 'plan-diff')) {
        ids.add(artifact.id)
      }
    }
    return ids
  }, [streamEvents, run.artifacts])
  const trailingArtifacts = stream.unplacedArtifacts.filter(
    (artifact) => !inlinePlanArtifactIds.has(artifact.id)
  )
  // The most recent complete attempt recording is the primary evidence in a
  // terminal outcome summary. The timeline keeps other recordings, but never
  // repeats this same player.
  const primaryRecording = useMemo(
    () =>
      run.state === 'terminal'
        ? [...run.artifacts]
            .filter((artifact) => artifact.kind === 'recording')
            .sort(
              (left, right) => (toTime(right.createdAt) ?? 0) - (toTime(left.createdAt) ?? 0)
            )[0]
        : undefined,
    [run.artifacts, run.state]
  )

  return (
    <>
      <BackLink href="#/runs" label="Runs" />

      {/* Verdict — the answer first. */}
      <header className="mb-8 max-w-3xl">
        <div className="flex items-center gap-3">
          <RunAvatar
            run={run}
            emoji={scenarioEmoji(run.scenarioId)}
            size="large"
            variant="centered"
          />
          <h1 className="text-[28px] font-semibold tracking-tight text-balance">
            {classification ? classification.verdict : verdict.label}
          </h1>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <StatusInline meta={verdict} active={verdict.active} quietWhenHealthy={false} />
          {classification ? (
            <span className={cn('text-[13px] font-medium', toneText[classification.tone])}>
              {classification.label}
              {run.diagnosis
                ? ` · ${Math.round(run.diagnosis.confidence * 100)}% confidence`
                : null}
            </span>
          ) : null}
          <MetaLine
            items={[
              <span key="scenario" className="font-mono">
                {run.scenarioId}
              </span>,
              sourceLabel(run),
              run.durationMs === undefined ? undefined : formatDuration(run.durationMs),
              relativeTime(run.createdAt, now)
            ]}
          />
        </div>

        {run.state !== 'terminal' && run.currentStageSummary ? (
          <p className="mt-4 max-w-prose text-[15px] leading-7">{run.currentStageSummary}</p>
        ) : null}

        {active && (run.diagnosis || run.reproductionCommand) ? (
          <Card className="mt-5 max-w-2xl gap-0 rounded-lg py-0">
            <CardContent className="space-y-4 px-4 py-4 sm:px-5 sm:py-5">
              {run.diagnosis ? (
                <>
                  <p className="max-w-prose text-[15px] leading-7">{run.diagnosis.summary}</p>
                  <p className="max-w-prose text-sm">
                    <span className="font-medium">Next action</span>{' '}
                    <span className="text-muted-foreground">{run.diagnosis.nextAction}</span>
                  </p>
                </>
              ) : null}
              {run.reproductionCommand ? (
                <CodeBlock copyText={run.reproductionCommand}>{run.reproductionCommand}</CodeBlock>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {run.state === 'terminal' && run.status === 'needs-review' ? (
          <a
            href={`#/runs/${encodeURIComponent(run.id)}/review`}
            className="mt-5 inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3.5 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            <Eye aria-hidden className="size-3.5" />
            Review the proposed test
          </a>
        ) : null}
      </header>

      {!active ? (
        <section aria-label="Run summary" className="mb-12 max-w-3xl">
          <Card data-run-summary className="gap-0 rounded-lg py-0">
            <CardContent className="space-y-5 px-4 py-4 sm:px-5 sm:py-5">
              <div>
                <SectionLabel>Outcome and evidence</SectionLabel>
                {run.currentStageSummary ? (
                  <p className="mt-1 max-w-prose text-[15px] leading-7">
                    {run.currentStageSummary}
                  </p>
                ) : null}
              </div>
              {run.diagnosis ? (
                <div className="max-w-prose space-y-2">
                  <p className="text-[15px] leading-7">{run.diagnosis.summary}</p>
                  <p className="text-sm">
                    <span className="font-medium">Next action</span>{' '}
                    <span className="text-muted-foreground">{run.diagnosis.nextAction}</span>
                  </p>
                </div>
              ) : null}
              {primaryRecording ? (
                <RecordingPlayer runId={run.id} artifact={primaryRecording} run={run} primary />
              ) : null}
              {run.reproductionCommand ? (
                <div>
                  <p className="mb-2 text-sm font-medium">Reproduce locally</p>
                  <CodeBlock copyText={run.reproductionCommand}>
                    {run.reproductionCommand}
                  </CodeBlock>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </section>
      ) : null}

      {/* Live progress — where the run is, and whether the agent is still
          working the test out or already replaying it. Updates in place with
          a fixed structure so nothing below it reflows. */}
      {showLivePanel ? (
        <section aria-label="Live progress" className="mb-12 max-w-3xl">
          <Card className="gap-0 rounded-lg py-0">
            <CardContent className="space-y-4 px-4 py-4 sm:px-5 sm:py-5">
              <LiveAnnouncement sentence={spine.sentence} />
              {feed ? <LiveStatusControls feed={feed} /> : null}
              {spine.cancelling ? <CancellingNotice /> : null}
              <PhaseSpine spine={spine} />
              <p className="text-[13px] text-muted-foreground">{runOriginSentence(run)}</p>
            </CardContent>
          </Card>
        </section>
      ) : null}

      {showJourney ? (
        <section aria-label="How this run unfolded" className="mb-12 max-w-3xl">
          <SectionLabel>How this run unfolded</SectionLabel>
          <div className="mt-2">
            <PhaseSpine spine={spine} showSentence={false} />
          </div>
          <p className="mt-2 text-[13px] text-muted-foreground">{runOriginSentence(run)}</p>
        </section>
      ) : null}

      {/* The evidence timeline groups the reader's work by attempt rather than
          making them reconstruct attempt boundaries from one long event rail. */}
      <section aria-label="What happened, in order" className="mt-12 max-w-3xl">
        <SectionLabel>{active ? 'What happened' : 'Attempts'}</SectionLabel>
        <p className="mb-6 text-sm text-muted-foreground">
          Each attempt keeps its steps and evidence together. Times count from the start of the run;
          technical platform events stay available inside their relevant step.
        </p>
        <JumpLine targets={jumpTargets} />
        {hasNarrative ? (
          <ol className="space-y-10">
            {stream.blocks.map((block) => (
              <AttemptBlock
                key={`attempt-${block.attempt.id}`}
                section={block}
                run={run}
                start={stream.start}
                primaryRecordingId={primaryRecording?.id}
              />
            ))}
          </ol>
        ) : active ? (
          <EmptyState>
            Nothing has been reported yet — the narrative appears here as the run progresses, and
            new entries always append at the bottom.
          </EmptyState>
        ) : run.state === 'terminal' && run.status === 'passed' ? (
          <PassedRunSummary run={run} />
        ) : (
          <EmptyState>No step-by-step narrative was recorded for this run.</EmptyState>
        )}

        {stream.unscopedEvents.length > 0 ? (
          <details className="group mt-5 border-t pt-3">
            <summary className="inline-flex min-h-9 cursor-pointer list-none items-center gap-2 text-[13px] font-medium text-muted-foreground select-none [&::-webkit-details-marker]:hidden">
              <span aria-hidden className="transition-transform group-open:rotate-90">
                ›
              </span>
              Run events not tied to a step ({stream.unscopedEvents.length})
            </summary>
            <ul className="mt-2 space-y-2">
              {stream.unscopedEvents.map((entry) => (
                <li key={entry.event.id}>
                  <EventNode event={entry.event} run={run} />
                </li>
              ))}
            </ul>
          </details>
        ) : null}

        {active && !spine.cancelling ? (
          <p className="mt-8 flex items-center gap-2.5 text-sm text-muted-foreground">
            <LoaderCircle aria-hidden className="size-3.5 shrink-0 motion-safe:animate-spin" />
            {feed?.paused
              ? 'Paused — new activity is buffered until you resume.'
              : 'Watching — new activity appends here without moving your view.'}
          </p>
        ) : null}

        {trailingArtifacts.filter((artifact) => artifact.id !== primaryRecording?.id).length > 0 ? (
          <section aria-label="Other captured files" className="mt-10">
            <Card className="gap-0 rounded-lg py-0">
              <CardContent className="px-4 py-4 sm:px-5">
                <SectionLabel>Also captured</SectionLabel>
                <ul className="mt-2 space-y-1">
                  {trailingArtifacts
                    .filter((artifact) => artifact.id !== primaryRecording?.id)
                    .map((artifact) => (
                      <li key={artifact.id}>
                        <ArtifactFileRow runId={run.id} artifact={artifact} />
                      </li>
                    ))}
                </ul>
              </CardContent>
            </Card>
          </section>
        ) : null}
      </section>

      <details className="group mt-16 max-w-2xl">
        <summary className="inline-flex h-9 cursor-pointer list-none items-center gap-1.5 text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase select-none [&::-webkit-details-marker]:hidden">
          <span aria-hidden className="transition-transform group-open:rotate-90">
            ›
          </span>
          Run reference — ids, hashes, timestamps
        </summary>
        <div className="mt-3">
          <KeyValueList
            items={[
              { key: 'Run id', value: <span className="font-mono">{run.id}</span> },
              run.suiteRunId
                ? { key: 'Suite run', value: <span className="font-mono">{run.suiteRunId}</span> }
                : undefined,
              { key: 'Initiated by', value: run.initiatedBy },
              { key: 'Origin', value: originLabel[run.origin] ?? titleCase(run.origin) },
              { key: 'Target', value: targetLabel[run.target] ?? titleCase(run.target) },
              run.source.repository
                ? { key: 'Repository', value: run.source.repository }
                : undefined,
              run.source.sha
                ? {
                    key: 'Commit',
                    value: <span className="font-mono">{shortSha(run.source.sha)}</span>
                  }
                : undefined,
              { key: 'Created', value: formatTime(run.createdAt) },
              run.finishedAt ? { key: 'Finished', value: formatTime(run.finishedAt) } : undefined,
              {
                key: 'Scenario hash',
                value: <span className="font-mono">{shortSha(run.scenarioHash)}</span>
              },
              run.planHash
                ? {
                    key: 'Plan hash',
                    value: <span className="font-mono">{shortSha(run.planHash)}</span>
                  }
                : undefined,
              {
                key: 'Evidence retention',
                value: artifactRetentionLabel(run.artifacts, now)
              },
              ...run.modelUsage.map((usage, index) => ({
                key: index === 0 ? 'Model usage' : `Model usage ${index + 1}`,
                value: `${usage.model} · ${usage.latencyMs}ms${
                  usage.inputTokens === undefined
                    ? ''
                    : ` · ${usage.inputTokens}→${usage.outputTokens ?? 0} tokens`
                }`
              }))
            ]}
          />
        </div>
      </details>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Stream rendering                                                    */
/* ------------------------------------------------------------------ */

/** Shared row shell: mono time offset on the left, content on the rail. */
function StreamRow({
  at,
  start,
  children,
  className
}: {
  at: number
  start: number
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex gap-3 sm:gap-4', className)}>
      <span
        aria-hidden
        className="w-10 shrink-0 pt-0.5 text-right font-mono text-xs text-muted-foreground tabular-nums"
      >
        {formatOffset(at, start)}
      </span>
      <span className="sr-only">{formatOffset(at, start)} from run start —</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function AttemptBlock({
  section,
  run,
  start,
  primaryRecordingId
}: {
  section: AttemptSection
  run: WebTestRun
  start: number
  primaryRecordingId?: string
}) {
  const { attempt } = section
  const status = attempt.status ? runStatusMeta[attempt.status] : undefined
  return (
    <li data-attempt-card className="list-none">
      <Card className="gap-0 rounded-lg py-0">
        <CardContent className="px-4 py-4 sm:px-5 sm:py-5">
          {/* Attempt boundary — a stable, independently scannable chapter. */}
          <div className="flex gap-3 sm:gap-4">
            <span
              aria-hidden
              className="w-10 shrink-0 pt-0.5 text-right font-mono text-xs text-muted-foreground tabular-nums"
            >
              {formatOffset(section.at, start)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3
                  id={`attempt-${attempt.id}`}
                  tabIndex={-1}
                  className="scroll-mt-4 text-base font-semibold"
                >
                  Attempt {section.index} of {section.total} · {titleCase(attempt.purpose)}
                </h3>
                {status ? <StatusInline meta={status} quietWhenHealthy={false} /> : null}
                <MetaLine
                  items={[
                    attempt.durationMs === undefined
                      ? undefined
                      : formatDuration(attempt.durationMs)
                  ]}
                />
              </div>
              {section.startSummary ? (
                <p className="mt-0.5 text-[13px] text-muted-foreground">{section.startSummary}</p>
              ) : null}
              {attempt.error ? (
                <p className="mt-3 max-w-prose rounded-md bg-status-fail-surface px-3 py-2 text-sm leading-6 break-words text-status-fail">
                  {attempt.error}
                </p>
              ) : null}
            </div>
          </div>

          <ol className="mt-4 space-y-3">
            {section.items.map((item) => {
              if (item.kind === 'quiet-steps') {
                return (
                  <li key={`quiet-${item.steps[0]!.step.id}`}>
                    <StreamRow at={item.at} start={start}>
                      <QuietStepsDisclosure group={item} start={start} />
                    </StreamRow>
                  </li>
                )
              }
              if (item.kind === 'event') {
                return (
                  <li key={item.event.id}>
                    <StreamRow at={item.at} start={start}>
                      <EventNode event={item.event} run={run} />
                    </StreamRow>
                  </li>
                )
              }
              if (item.kind === 'artifact') {
                if (item.artifact.id === primaryRecordingId) return null
                return (
                  <li key={item.artifact.id}>
                    <StreamRow at={item.at} start={start}>
                      <StreamArtifact runId={run.id} artifact={item.artifact} run={run} />
                    </StreamRow>
                  </li>
                )
              }
              return (
                <li
                  key={item.step.id}
                  {...(item.step.status === 'failed'
                    ? { id: `step-${item.step.id}`, tabIndex: -1, className: 'scroll-mt-4' }
                    : {})}
                >
                  <StreamRow at={item.at} start={start}>
                    <StepBlock
                      entry={item}
                      runId={run.id}
                      primaryRecordingId={primaryRecordingId}
                    />
                  </StreamRow>
                </li>
              )
            })}
          </ol>
        </CardContent>
      </Card>
    </li>
  )
}

function QuietStepsDisclosure({ group, start }: { group: QuietStepsGroup; start: number }) {
  const total = group.steps.reduce((sum, entry) => sum + (entry.step.durationMs ?? 0), 0)
  return (
    <details className="group/quiet">
      <summary className="inline-flex min-h-9 cursor-pointer list-none items-center gap-2 text-sm text-muted-foreground select-none [&::-webkit-details-marker]:hidden">
        <span aria-hidden className="transition-transform group-open/quiet:rotate-90">
          ›
        </span>
        {group.steps.length} steps passed · {formatDuration(total)} — show each step
      </summary>
      <ol className="mt-1 space-y-2">
        {group.steps.map((entry) => (
          <li key={entry.step.id} className="flex gap-3 sm:gap-4">
            <span
              aria-hidden
              className="w-10 shrink-0 pt-0.5 text-right font-mono text-xs text-muted-foreground tabular-nums"
            >
              {formatOffset(entry.at, start)}
            </span>
            <div className="min-w-0 flex-1">
              <StepLine step={entry.step} />
            </div>
          </li>
        ))}
      </ol>
    </details>
  )
}

/** One step in the stream. Failures are unmistakable; evidence is inline. */
function StepBlock({
  entry,
  runId,
  primaryRecordingId
}: {
  entry: StepEntry
  runId: string
  primaryRecordingId?: string
}) {
  const failed = entry.step.status === 'failed'
  return (
    <div
      data-step-panel
      className={cn(
        'border-t border-border/70 pt-3',
        failed &&
          'border-status-fail/40 bg-status-fail-surface/15 px-3 pb-3 dark:bg-status-fail-surface/15'
      )}
    >
      <StepLine step={entry.step} emphasize={failed} />
      {failed ? <FailureDetail step={entry.step} /> : null}
      {entry.events.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {entry.events.map((event) => (
            <li key={event.id}>
              <EventLine event={event} />
            </li>
          ))}
        </ul>
      ) : null}
      {entry.artifacts.length > 0 ? (
        <div className="mt-4 space-y-5">
          {orderStepArtifacts(entry.artifacts)
            .filter((artifact) => artifact.id !== primaryRecordingId)
            .map((artifact) => (
              <StreamArtifact key={artifact.id} runId={runId} artifact={artifact} />
            ))}
        </div>
      ) : null}
    </div>
  )
}

function StepLine({ step, emphasize = false }: { step: RunStepResult; emphasize?: boolean }) {
  const meta = stepStatusMeta[step.status]
  const Icon = meta.icon
  const quiet = meta.tone === 'pass' || meta.tone === 'neutral'
  return (
    <div className="flex items-baseline gap-2.5">
      <Icon
        aria-hidden
        className={cn(
          'size-3.5 shrink-0 self-center',
          quiet ? 'text-muted-foreground' : toneText[meta.tone],
          step.status === 'running' && 'animate-spin'
        )}
      />
      <span className="min-w-0 flex-1">
        <span className={cn('text-sm', emphasize && 'text-[15px] font-semibold')}>
          {step.description}
        </span>
        <span
          className={cn(
            'ml-2 text-[13px] font-medium',
            quiet ? 'text-muted-foreground' : toneText[meta.tone]
          )}
        >
          {meta.label}
        </span>
        <span className="mt-0.5 block">
          <MetaLine
            items={[
              stepPhaseLabel(step),
              step.actionSummary,
              step.locatorSummary,
              step.durationMs === undefined ? undefined : formatDuration(step.durationMs)
            ]}
          />
        </span>
      </span>
    </div>
  )
}

function FailureDetail({ step }: { step: RunStepResult }) {
  return (
    <div className="mt-3">
      {step.expected !== undefined || step.actual !== undefined ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {step.expected !== undefined ? (
            <div>
              <p className="text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase">
                Expected
              </p>
              <p className="mt-1 rounded-md bg-muted px-3 py-2 font-mono text-[13px] break-words">
                {step.expected}
              </p>
            </div>
          ) : null}
          {step.actual !== undefined ? (
            <div>
              <p className="text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase">
                Actual
              </p>
              <p
                className={cn(
                  'mt-1 rounded-md bg-status-fail-surface px-3 py-2 font-mono text-[13px] break-words',
                  toneText.fail
                )}
              >
                {step.actual}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
      {step.error ? (
        <p className="mt-3 border-l-2 border-status-fail pl-3 text-sm leading-6 break-words">
          {step.error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * A block-level event. Most render as one narrated line; an event announcing
 * a candidate plan or plan diff renders as an inline review card — the plan
 * taking shape is first-class content, not a bare download.
 */
function EventNode({ event, run }: { event: RunEvent; run: WebTestRun }) {
  const artifact =
    event.artifactId !== undefined
      ? run.artifacts.find((candidate) => candidate.id === event.artifactId)
      : undefined
  if (artifact && (artifact.kind === 'candidate-plan' || artifact.kind === 'plan-diff')) {
    return <PlanArtifactCard run={run} artifact={artifact} event={event} />
  }
  return <EventLine event={event} />
}

/**
 * The moment the agent's work becomes reviewable. Links into the candidate
 * review surface; the honest download stays available too.
 */
function PlanArtifactCard({
  run,
  artifact,
  event
}: {
  run: WebTestRun
  artifact: ArtifactRef
  event: RunEvent
}) {
  const reviewable = run.state === 'terminal' && run.status === 'needs-review'
  return (
    <div className="max-w-xl rounded-md border p-4">
      <p className="flex items-center gap-2 text-sm font-medium">
        <FileDiff aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        {artifactKindLabel[artifact.kind] ?? titleCase(artifact.kind)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{event.summary}</p>
      <p className="mt-1">
        <MetaLine
          items={[
            <span key="name" className="font-mono">
              {artifact.name}
            </span>,
            formatBytes(artifact.byteSize)
          ]}
        />
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a
          href={`#/runs/${encodeURIComponent(run.id)}/review`}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
            reviewable
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'text-primary hover:bg-accent'
          )}
        >
          <Eye aria-hidden className="size-3.5" />
          {reviewable ? 'Review this plan' : 'Preview the candidate plan'}
        </a>
        <a
          href={artifactDownloadUrl(run.id, artifact)}
          download={artifact.name}
          className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          <Download aria-hidden className="size-3.5" />
          Download
        </a>
      </div>
    </div>
  )
}

/** A platform event in the stream, with its raw JSON one disclosure away. */
function EventLine({ event }: { event: RunEvent }) {
  return (
    <details className="group/event">
      <summary className="inline-flex min-h-6 cursor-pointer list-none items-baseline gap-2.5 select-none [&::-webkit-details-marker]:hidden">
        <span
          aria-hidden
          className="size-1.5 shrink-0 self-center rounded-full bg-muted-foreground/60"
        />
        <span className="min-w-0 text-sm text-muted-foreground">
          {event.summary}
          <span className="ml-2 font-mono text-xs">
            {event.type} · #{event.sequence}
            <span
              aria-hidden
              className="ml-1.5 inline-block transition-transform group-open/event:rotate-90"
            >
              ›
            </span>
            <span className="sr-only"> — toggle raw event JSON</span>
          </span>
        </span>
      </summary>
      <pre className="mt-1.5 max-w-full overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs leading-5">
        {JSON.stringify(event, null, 2)}
      </pre>
    </details>
  )
}

/** Failure screenshot first, then diff, baseline, before/after. */
function orderStepArtifacts(artifacts: ArtifactRef[]): ArtifactRef[] {
  const order = (artifact: ArtifactRef): number => {
    if (artifact.kind === 'screenshot-failure') return 0
    if (artifact.kind === 'screenshot-diff') return 1
    if (artifact.kind === 'screenshot-baseline') return 2
    if (artifact.kind === 'screenshot-after') return 3
    if (artifact.kind === 'screenshot-before') return 4
    if (artifact.kind === 'recording') return 5
    return 6
  }
  return [...artifacts].sort((left, right) => order(left) - order(right))
}

/**
 * An artifact rendered at its moment in the stream: screenshots inline
 * (annotated when we know what diverged), recordings as a real player,
 * everything else as an honest labelled download — never a fake frame.
 */
function StreamArtifact({
  runId,
  artifact,
  run
}: {
  runId: string
  artifact: ArtifactRef
  run?: WebTestRun
}) {
  if (artifact.kind.startsWith('screenshot-')) {
    return <ScreenshotArtifact runId={runId} artifact={artifact} />
  }
  if (artifact.kind === 'recording') {
    return <RecordingPlayer runId={runId} artifact={artifact} run={run} />
  }
  return <ArtifactFileRow runId={runId} artifact={artifact} />
}

/**
 * Every screenshot kind is renderable. Fixture-only annotations may decorate
 * committed image assets, but live evidence always comes from the authenticated
 * verifier artifact endpoint.
 */
function ScreenshotArtifact({ runId, artifact }: { runId: string; artifact: ArtifactRef }) {
  const [failedToLoad, setFailedToLoad] = useState(false)
  const fixture = fixtureArtifactImages[artifact.id]
  const kindLabel = screenshotKindShortLabel[artifact.kind] ?? titleCase(artifact.kind)
  const isFailure = artifact.kind === 'screenshot-failure'
  const source = fixture?.url ?? artifactDownloadUrl(runId, artifact)

  return (
    <figure className={cn(!isFailure && 'max-w-md')}>
      <div className="relative overflow-hidden rounded-lg bg-muted ring-1 ring-border">
        {failedToLoad ? (
          <div className="flex aspect-[16/10] flex-col items-start justify-center gap-3 px-4 py-5 text-sm">
            <p className="font-medium">This screenshot could not be displayed.</p>
            <p className="text-[13px] leading-5 text-muted-foreground">
              It may have expired, been retained out, or use a format this browser cannot decode.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href={source}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center rounded-md px-2.5 text-[13px] font-medium text-primary transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                Open image
              </a>
              <a
                href={source}
                download={artifact.name}
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                <Download aria-hidden className="size-3.5" />
                Download
              </a>
            </div>
          </div>
        ) : (
          <>
            <img
              src={source}
              alt={`${kindLabel} screenshot: ${artifact.name}`}
              loading="lazy"
              onError={() => setFailedToLoad(true)}
              className="block aspect-[16/10] w-full object-cover"
            />
            {fixture?.annotation ? (
              <div
                className="absolute rounded-sm border-2 border-status-fail"
                style={{
                  left: fixture.annotation.left,
                  top: fixture.annotation.top,
                  width: fixture.annotation.width,
                  height: fixture.annotation.height
                }}
              >
                <span className="absolute top-full left-0 mt-1.5 rounded-sm bg-status-fail-surface px-2 py-1 text-xs font-medium whitespace-nowrap text-status-fail max-sm:whitespace-normal">
                  {fixture.annotation.label}
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
      <figcaption className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[13px] font-medium">{kindLabel} screenshot</span>
        <span className="font-mono text-[13px] text-muted-foreground">{artifact.name}</span>
        {artifact.redacted ? (
          <span className="text-[13px] text-muted-foreground">Redacted</span>
        ) : null}
      </figcaption>
    </figure>
  )
}

/**
 * Video is first-class evidence: an inline player with controls, no
 * autoplay, and a shortcut that jumps the playhead to the failing moment.
 */
function RecordingPlayer({
  runId,
  artifact,
  run,
  primary = false
}: {
  runId: string
  artifact: ArtifactRef
  run?: WebTestRun
  primary?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const fixture = fixtureArtifactVideos[artifact.id]
  const source = fixture?.url ?? artifactDownloadUrl(runId, artifact)
  const failingMoment = fixture?.failingMomentSeconds
  const scenario = run?.scenarioId
  return (
    <figure>
      <div className="overflow-hidden rounded-lg bg-muted ring-1 ring-border">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- run recordings have no audio track */}
        <video
          ref={videoRef}
          controls
          preload="auto"
          playsInline
          poster={fixture?.poster}
          aria-label={`Video recording of this attempt${scenario ? ` — ${scenario}` : ''}`}
          className="block aspect-[16/10] w-full"
        >
          <source src={source} type={artifact.contentType} />
          Your browser cannot play this recording —{' '}
          <a href={artifactDownloadUrl(runId, artifact)} download={artifact.name}>
            download {artifact.name}
          </a>
          .
        </video>
      </div>
      <figcaption className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="inline-flex items-center gap-1.5 text-[13px] font-medium">
          <Film aria-hidden className="size-3.5 text-muted-foreground" />
          {primary ? 'Primary recording of the final attempt' : 'Recording of the whole attempt'}
        </span>
        <span className="font-mono text-[13px] text-muted-foreground">
          {artifact.name} · {formatBytes(artifact.byteSize)}
        </span>
        {failingMoment !== undefined ? (
          <button
            type="button"
            onClick={() => {
              const video = videoRef.current
              if (!video) return
              video.currentTime = failingMoment
              void video.play().catch(() => undefined)
            }}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-primary transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            Jump to the failing moment ({Math.floor(failingMoment / 60)}:
            {String(failingMoment % 60).padStart(2, '0')})
          </button>
        ) : null}
      </figcaption>
    </figure>
  )
}

/** Honest affordance for non-renderable artifacts — a labelled download. */
function ArtifactFileRow({ runId, artifact }: { runId: string; artifact: ArtifactRef }) {
  const Icon = artifactIcon(artifact.kind)
  return (
    <div className="flex items-center gap-3 py-1">
      <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">
          {artifactKindLabel[artifact.kind] ?? titleCase(artifact.kind)}
        </span>
        <MetaLine
          items={[
            <span key="name" className="font-mono">
              {artifact.name}
            </span>,
            formatBytes(artifact.byteSize),
            artifact.redacted ? 'Redacted' : undefined
          ]}
        />
      </span>
      <a
        href={artifactDownloadUrl(runId, artifact)}
        download={artifact.name}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <Download aria-hidden className="size-3.5" />
        Download
      </a>
    </div>
  )
}
